// CollapsableLayout.jsx - Enhanced with Smart Auto-Hiding Bottom Navigation

import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../store/thunks/authThunks";
import {
  menuConfig,
  quickAccessItems,
  getFilteredMenuConfig,
  getFilteredQuickAccessItems,
  GROUP_COLORS,
  DEFAULT_FEATURES_CONFIG,
} from "../../config/menuConfig";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LogoutIcon,
  SearchIcon,
  TranslateIcon,
  QuestionMarkCircleIcon,
  XIcon,
  UserCircleIcon,
} from "@heroicons/react/outline";
import Header from "./Header";
import BottomTabNavigation, {
  BOTTOM_NAV_HEIGHT_VAR,
  useBottomNav,
} from "./BottomTabNavigation";
import Button from "../common/Button";
import { useI18n } from "../../hooks/useI18n";
import ReactDOM from "react-dom";

// ============================================
// CONSTANTS
// ============================================

const BOTTOM_NAV_FALLBACK = 80;

// ============================================
// HOOKS
// ============================================

// Custom hook for responsive breakpoints
const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState("desktop");

  useEffect(() => {
    const checkBreakpoint = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setBreakpoint("mobile");
      } else if (width < 1024) {
        setBreakpoint("tablet");
      } else {
        setBreakpoint("desktop");
      }
    };

    checkBreakpoint();
    window.addEventListener("resize", checkBreakpoint);
    return () => window.removeEventListener("resize", checkBreakpoint);
  }, []);

  return {
    isMobile: breakpoint === "mobile",
    isTablet: breakpoint === "tablet",
    isDesktop: breakpoint === "desktop",
    breakpoint,
  };
};

// Portal component for rendering submenu outside the main DOM hierarchy
const SubmenuPortal = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return ReactDOM.createPortal(children, document.body);
};

// ============================================
// LAYOUT CONTEXT
// ============================================

export const LayoutContext = React.createContext({
  isMobile: false,
  bottomNavHeight: 0,
  hasBottomNav: false,
  isNavHidden: false,
});

export const useLayout = () => React.useContext(LayoutContext);

// ============================================
// MAIN COMPONENT
// ============================================

const CollapsableLayout = () => {
  // Get i18n context from Redux
  const { t, language, changeLanguage } = useI18n();
  const { isMobile, isTablet, isDesktop } = useBreakpoint();

  // State Management
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showHelper, setShowHelper] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);

  // Add transition state to handle overflow during animation
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Add these new state variables at the top with other state declarations
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [hoverIntentItem, setHoverIntentItem] = useState(null);
  const [isMouseOverSubmenu, setIsMouseOverSubmenu] = useState(false);
  const [isMouseOverButton, setIsMouseOverButton] = useState(false);

  // NEW: State for submenu positioning
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  const [submenuAlignment, setSubmenuAlignment] = useState("top");

  // NEW: Refs for measuring elements
  const menuGroupRefs = useRef({});
  const submenuRef = useRef(null);

  // Feature configuration state
  const [featuresConfig, setFeaturesConfig] = useState({
    showItemDescriptions: false,
    showGroupDescriptions: true,
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const {
    isRootAdmin,
    permissions = [],
    user,
  } = useSelector((state) => state.auth);

  // Get filtered menu based on permissions and features config
  const filteredMenuConfig = getFilteredMenuConfig(
    permissions,
    isRootAdmin,
    featuresConfig,
  );
  const filteredQuickAccess = getFilteredQuickAccessItems(
    permissions,
    isRootAdmin,
    featuresConfig,
  );

  // Features config with defaults
  const features = { ...DEFAULT_FEATURES_CONFIG, ...featuresConfig };

  // Layout context value for child components
  const layoutContextValue = React.useMemo(
    () => ({
      isMobile,
      isTablet,
      isDesktop,
      hasBottomNav: isMobile,
    }),
    [isMobile, isTablet, isDesktop],
  );

  // Auto-collapse menu on tablet
  useEffect(() => {
    if (isTablet && !isMenuCollapsed) {
      setIsMenuCollapsed(true);
    }
  }, [isTablet]);

  // Initialize expanded groups
  useEffect(() => {
    const saved = localStorage.getItem("menuGroupsState");
    if (saved) {
      setExpandedGroups(JSON.parse(saved));
    } else {
      const defaultState = {};
      menuConfig.forEach((group) => {
        defaultState[group.id] = group.expanded !== false;
      });
      setExpandedGroups(defaultState);
    }
  }, []);

  // Show helper for new users
  useEffect(() => {
    const hasSeenHelper = localStorage.getItem("hasSeenMenuHelper");
    if (!hasSeenHelper && features.tooltips) {
      setShowHelper(true);
      setTimeout(() => {
        setShowHelper(false);
        localStorage.setItem("hasSeenMenuHelper", "true");
      }, 5000);
    }
  }, [features.tooltips]);

  // Prevent body scroll when submenu is open
  useEffect(() => {
    if (hoveredItem && isMenuCollapsed) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflowX = "hidden";

      return () => {
        document.body.style.overflowX = originalStyle;
      };
    }
  }, [hoveredItem, isMenuCollapsed]);

  const handleLogout = () => {
    const confirmLogout = window.confirm(t("header.logout_confirm"));
    if (confirmLogout) {
      dispatch(logout());
    }
  };

  const isActiveLink = (path) => {
    return location.pathname === path;
  };

  const isGroupActive = (group) => {
    return group.items.some((item) => location.pathname === item.path);
  };

  const toggleMenuCollapse = () => {
    if (features.menuCollapsing) {
      setIsTransitioning(true);
      setIsMenuCollapsed(!isMenuCollapsed);
      setHoveredItem(null);
      setHoverIntentItem(null);

      setTimeout(() => {
        setIsTransitioning(false);
      }, 400);

      if (features.tooltips) {
        setShowHelper(true);
        setTimeout(() => setShowHelper(false), 3000);
      }
    }
  };

  const toggleGroup = (groupId) => {
    const newState = {
      ...expandedGroups,
      [groupId]: !expandedGroups[groupId],
    };
    setExpandedGroups(newState);
    localStorage.setItem("menuGroupsState", JSON.stringify(newState));
  };

  // Filter menu items based on search
  const getFilteredItems = () => {
    if (!searchQuery) return filteredMenuConfig;

    const query = searchQuery.toLowerCase();
    return filteredMenuConfig
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          const name = t(item.translationKey).toLowerCase();
          return name.includes(query);
        }),
      }))
      .filter((group) => group.items.length > 0);
  };

  // Calculate optimal position for submenu with viewport constraints
  const calculateSubmenuPosition = useCallback(
    (groupId) => {
      const groupElement = menuGroupRefs.current[groupId];
      if (!groupElement) return;

      const groupRect = groupElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const group = filteredMenuConfig.find((g) => g.id === groupId);
      const estimatedItemHeight = features.showItemDescriptions ? 60 : 40;
      const headerHeight = 60;
      const padding = 24;
      const estimatedSubmenuHeight = Math.min(
        (group?.items.length || 0) * estimatedItemHeight +
          headerHeight +
          padding,
        viewportHeight * 0.8,
      );
      const submenuWidth = 280;

      const spaceAbove = groupRect.top;
      const spaceBelow = viewportHeight - groupRect.bottom;
      const centerY = groupRect.top + groupRect.height / 2;

      let top, left, alignment;

      if (estimatedSubmenuHeight <= viewportHeight * 0.8) {
        if (
          centerY - estimatedSubmenuHeight / 2 > 60 &&
          centerY + estimatedSubmenuHeight / 2 < viewportHeight - 20
        ) {
          top = centerY - estimatedSubmenuHeight / 2;
          alignment = "center";
        } else if (
          spaceBelow >= estimatedSubmenuHeight ||
          spaceBelow > spaceAbove
        ) {
          top = Math.min(
            groupRect.top,
            viewportHeight - estimatedSubmenuHeight - 20,
          );
          alignment = "top";
        } else {
          top = Math.max(60, groupRect.bottom - estimatedSubmenuHeight);
          alignment = "bottom";
        }
      } else {
        top = 80;
        alignment = "top";
      }

      top = Math.max(
        10,
        Math.min(top, viewportHeight - estimatedSubmenuHeight - 10),
      );

      left = groupRect.right + 8;

      if (left + submenuWidth > viewportWidth - 10) {
        left = Math.max(10, viewportWidth - submenuWidth - 10);
      }

      setSubmenuPosition({ top, left });
      setSubmenuAlignment(alignment);
    },
    [filteredMenuConfig, features.showItemDescriptions],
  );

  // Handle hover intent with position calculation
  const handleMenuHover = useCallback(
    (itemId, isEntering, isGroup = false, source = "button") => {
      if (!features.tooltips || isTransitioning) return;

      if (isEntering) {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          setHoverTimeout(null);
        }

        if (source === "button") {
          setIsMouseOverButton(true);
        } else if (source === "submenu") {
          setIsMouseOverSubmenu(true);
        }

        if (isGroup && isMenuCollapsed) {
          calculateSubmenuPosition(itemId);
        }

        setHoveredItem(itemId);
        setHoverIntentItem(itemId);
      } else {
        if (source === "button") {
          setIsMouseOverButton(false);
        } else if (source === "submenu") {
          setIsMouseOverSubmenu(false);
        }

        const timeout = setTimeout(() => {
          setIsMouseOverButton((current) => {
            setIsMouseOverSubmenu((currentSubmenu) => {
              if (!current && !currentSubmenu && hoverIntentItem === itemId) {
                setHoveredItem(null);
                setHoverIntentItem(null);
              }
              return currentSubmenu;
            });
            return current;
          });
        }, 500);
        setHoverTimeout(timeout);
      }
    },
    [
      features.tooltips,
      hoverTimeout,
      hoverIntentItem,
      isMenuCollapsed,
      calculateSubmenuPosition,
      isTransitioning,
    ],
  );

  const renderQuickAccess = () => (
    <div
      className={`p-3 space-y-2 ${isTransitioning ? "overflow-hidden" : ""}`}
    >
      {filteredQuickAccess.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className={`
            relative flex items-center transition-all duration-300 transform
            ${item.isSpecial ? item.color : "bg-primary-dark bg-opacity-20"}
            ${
              isMenuCollapsed
                ? "p-3 rounded-xl justify-center aspect-square"
                : "p-3 rounded-xl"
            }
            ${
              isActiveLink(item.path)
                ? "scale-105 shadow-lg ring-2 ring-white ring-opacity-50"
                : "hover:scale-105 hover:shadow-md"
            }
          `}
          onMouseEnter={() =>
            !isTransitioning && handleMenuHover(item.path, true)
          }
          onMouseLeave={() =>
            !isTransitioning && handleMenuHover(item.path, false)
          }
        >
          <item.icon
            className={`${
              isMenuCollapsed ? "w-7 h-7" : "w-6 h-6"
            } text-text-on-primary flex-shrink-0`}
          />
          {!isMenuCollapsed && (
            <span className="ml-3 font-bold text-text-on-primary text-sm whitespace-nowrap overflow-hidden text-ellipsis">
              {t(item.translationKey)}
            </span>
          )}

          {/* Tooltip for collapsed state */}
          {isMenuCollapsed &&
            hoveredItem === item.path &&
            features.tooltips &&
            !isTransitioning && (
              <div className="absolute left-full ml-3 px-3 py-2 bg-neutral-dark text-white text-sm rounded-lg whitespace-nowrap z-50 shadow-xl">
                <div className="font-bold">{t(item.translationKey)}</div>
                {item.descriptionKey && features.showItemDescriptions && (
                  <div className="text-xs opacity-80">
                    {t(item.descriptionKey)}
                  </div>
                )}
              </div>
            )}

          {/* Active indicator */}
          {isActiveLink(item.path) && (
            <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-text-on-primary rounded-r-full" />
          )}
        </Link>
      ))}
    </div>
  );

  const renderMenuItem = (item, groupColor) => (
    <Link
      key={item.path}
      to={item.path}
      className={`
        relative flex items-center transition-all duration-200 rounded-lg group
        ${isMenuCollapsed ? "p-3 justify-center" : "px-3 py-2.5 mx-2"}
        ${
          isActiveLink(item.path)
            ? "bg-white bg-opacity-20 shadow-md transform scale-105"
            : "hover:bg-white hover:bg-opacity-10 hover:transform hover:scale-102"
        }
      `}
      onMouseEnter={() => !isTransitioning && handleMenuHover(item.path, true)}
      onMouseLeave={() => !isTransitioning && handleMenuHover(item.path, false)}
    >
      {/* Badge */}
      {item.badge && features.groupBadges && !isMenuCollapsed && (
        <div
          className={`absolute right-3 ${
            item.badgeColor || "bg-danger"
          } text-white text-xs rounded-full px-1.5 py-0.5 animate-pulse`}
        >
          {item.badge.count}
        </div>
      )}

      <item.icon
        className={`${
          isMenuCollapsed ? "w-6 h-6" : "w-5 h-5"
        } text-text-on-primary flex-shrink-0`}
      />

      {!isMenuCollapsed && (
        <div className="ml-3 flex-1 min-w-0 overflow-hidden">
          <div className="font-medium text-text-on-primary text-sm truncate">
            {t(item.translationKey)}
          </div>
          {item.descriptionKey && features.showItemDescriptions && (
            <div className="text-xs text-text-on-primary text-opacity-70 truncate">
              {t(item.descriptionKey)}
            </div>
          )}
        </div>
      )}

      {/* Tooltip for collapsed state */}
      {isMenuCollapsed &&
        hoveredItem === item.path &&
        features.tooltips &&
        !isTransitioning && (
          <div className="absolute left-full ml-3 px-3 py-2 bg-neutral-dark text-white text-sm rounded-lg whitespace-nowrap z-50 shadow-xl">
            <div className="font-bold">{t(item.translationKey)}</div>
            {item.descriptionKey && features.showItemDescriptions && (
              <div className="text-xs opacity-80">{t(item.descriptionKey)}</div>
            )}
          </div>
        )}

      {/* Active indicator */}
      {isActiveLink(item.path) && (
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-6 bg-text-on-primary rounded-r-full" />
      )}
    </Link>
  );

  const renderMenuGroup = (group) => {
    const isExpanded = expandedGroups[group.id] !== false;
    const groupActive = isGroupActive(group);
    const groupColor = group.color;

    if (isMenuCollapsed) {
      // Collapsed view
      return (
        <div
          key={group.id}
          ref={(el) => (menuGroupRefs.current[group.id] = el)}
          className="relative group mb-3 px-3"
          onMouseEnter={() =>
            !isTransitioning && handleMenuHover(group.id, true, true, "button")
          }
          onMouseLeave={() =>
            !isTransitioning && handleMenuHover(group.id, false, true, "button")
          }
        >
          <button
            onClick={() => toggleGroup(group.id)}
            className={`
              w-full p-3 cursor-pointer flex flex-col items-center justify-center rounded-xl
              transition-all duration-300 transform aspect-square
              ${
                groupActive
                  ? `${groupColor.bg} shadow-lg scale-105`
                  : `bg-primary-dark bg-opacity-30 hover:${groupColor.bg} hover:shadow-md hover:scale-105`
              }
            `}
          >
            <div
              className={`p-2 rounded-lg flex-shrink-0 ${
                groupActive ? "bg-opacity-20" : ""
              }`}
            >
              <group.icon className="w-6 h-6 text-text-on-primary flex-shrink-0" />
            </div>
            {group.badge && features.groupBadges && (
              <span className="absolute top-1 right-1 bg-danger text-white text-xs rounded-full px-1.5 animate-pulse">
                {group.badge.count}
              </span>
            )}
          </button>

          {/* Render submenu using portal to prevent overflow issues */}
          {hoveredItem === group.id &&
            features.tooltips &&
            !isTransitioning && (
              <SubmenuPortal>
                {/* Enhanced invisible bridge to maintain hover */}
                <div
                  className="fixed z-[998]"
                  style={{
                    top: `${submenuPosition.top}px`,
                    left: `${submenuPosition.left - 24}px`,
                    width: "24px",
                    height: "100%",
                    maxHeight: `${window.innerHeight * 0.8}px`,
                  }}
                  onMouseEnter={() =>
                    handleMenuHover(group.id, true, true, "submenu")
                  }
                  onMouseLeave={() =>
                    handleMenuHover(group.id, false, true, "submenu")
                  }
                />

                {/* Submenu */}
                <div
                  ref={submenuRef}
                  className="fixed bg-background rounded-xl shadow-2xl p-3 z-[999]"
                  style={{
                    top: `${submenuPosition.top}px`,
                    left: `${submenuPosition.left}px`,
                    width: "280px",
                    maxWidth: "calc(100vw - 120px)",
                    maxHeight: `${window.innerHeight * 0.8}px`,
                    animation: "fadeIn 0.2s ease-out",
                    contain: "layout style paint",
                  }}
                  onMouseEnter={() =>
                    handleMenuHover(group.id, true, true, "submenu")
                  }
                  onMouseLeave={() =>
                    handleMenuHover(group.id, false, true, "submenu")
                  }
                >
                  {/* Visual connector arrow */}
                  <div
                    className="absolute -left-2 w-4 h-4 bg-background transform rotate-45"
                    style={{
                      top:
                        submenuAlignment === "center"
                          ? "50%"
                          : submenuAlignment === "bottom"
                            ? "auto"
                            : "24px",
                      bottom: submenuAlignment === "bottom" ? "24px" : "auto",
                      transform:
                        submenuAlignment === "center"
                          ? "translateY(-50%) rotate(45deg)"
                          : "rotate(45deg)",
                      boxShadow: "-2px 2px 4px rgba(0,0,0,0.1)",
                    }}
                  />

                  <div
                    className={`font-bold ${groupColor.text} text-sm mb-2 pb-2 border-b ${groupColor.border} flex items-center justify-between`}
                  >
                    <span className="truncate pr-2">
                      {t(`${group.translationKey}.label`)}
                    </span>
                    <button
                      className="text-neutral-dark hover:text-text p-1 flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setHoveredItem(null);
                        setHoverIntentItem(null);
                        setIsMouseOverButton(false);
                        setIsMouseOverSubmenu(false);
                        if (hoverTimeout) {
                          clearTimeout(hoverTimeout);
                          setHoverTimeout(null);
                        }
                      }}
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Scrollable content area */}
                  <div
                    className="overflow-y-auto overflow-x-hidden custom-scrollbar"
                    style={{
                      maxHeight: `calc(${window.innerHeight * 0.7}px - 60px)`,
                      overscrollBehavior: "contain",
                    }}
                  >
                    <div className="space-y-1 pr-1">
                      {group.items.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`
                          flex items-center px-3 py-2 rounded-lg text-text text-sm
                          transition-all duration-200
                          ${
                            isActiveLink(item.path)
                              ? `${groupColor.light} ${groupColor.text} font-medium`
                              : `hover:${groupColor.light}`
                          }
                        `}
                          onClick={() => {
                            setHoveredItem(null);
                            setHoverIntentItem(null);
                            setIsMouseOverButton(false);
                            setIsMouseOverSubmenu(false);
                            if (hoverTimeout) {
                              clearTimeout(hoverTimeout);
                              setHoverTimeout(null);
                            }
                          }}
                        >
                          <item.icon
                            className={`w-4 h-4 mr-2 flex-shrink-0 ${
                              isActiveLink(item.path)
                                ? groupColor.text
                                : "text-neutral-dark"
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">
                              {t(item.translationKey)}
                            </div>
                            {item.descriptionKey &&
                              features.showItemDescriptions && (
                                <div className="text-xs text-neutral-dark truncate">
                                  {t(item.descriptionKey)}
                                </div>
                              )}
                          </div>
                          {item.badge && features.groupBadges && (
                            <span
                              className={`ml-2 flex-shrink-0 ${
                                item.badgeColor || "bg-danger"
                              } text-white text-xs rounded-full px-1.5 py-0.5`}
                            >
                              {item.badge.count}
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </SubmenuPortal>
            )}
        </div>
      );
    }

    // Expanded view with beautiful cards
    return (
      <div key={group.id} className="mb-4 px-3">
        {features.groupHeaders && (
          <button
            onClick={() => toggleGroup(group.id)}
            className={`
              w-full flex items-center justify-between px-4 py-3 rounded-lg
              transition-all duration-300 transform overflow-hidden
              ${
                groupActive
                  ? `${groupColor.bg} text-text-on-primary shadow-md`
                  : `bg-white bg-opacity-10 text-text-on-primary hover:bg-opacity-20`
              }
            `}
          >
            <div className="flex items-center min-w-0">
              <div
                className={`p-2 rounded-lg flex-shrink-0 aspect-square flex items-center justify-center ${
                  groupActive ? " bg-opacity-20" : " bg-opacity-10"
                }`}
              >
                <group.icon className="w-5 h-5 flex-shrink-0" />
              </div>
              <div className="ml-3 text-left min-w-0 overflow-hidden">
                <div className="font-bold text-sm truncate">
                  {t(`${group.translationKey}.label`)}
                </div>
                {features.showGroupDescriptions && (
                  <div className="text-xs opacity-80 truncate">
                    {t(`${group.translationKey}.description`)}
                  </div>
                )}
              </div>
              {group.badge && features.groupBadges && (
                <span className="ml-2 bg-danger text-white text-xs rounded-full px-1.5 py-0.5 animate-pulse flex-shrink-0">
                  {group.badge.count}
                </span>
              )}
            </div>
            <div className="flex-shrink-0 ml-2">
              {isExpanded ? (
                <ChevronUpIcon className="w-5 h-5" />
              ) : (
                <ChevronDownIcon className="w-5 h-5" />
              )}
            </div>
          </button>
        )}

        {/* Animated expansion with items */}
        <div
          className={`
            ${isTransitioning ? "overflow-hidden" : "overflow-visible"}
            ${
              features.animations
                ? "transition-all duration-300 ease-in-out"
                : ""
            } mt-2
            ${
              isExpanded || !features.groupHeaders
                ? "max-h-96 opacity-100"
                : "max-h-0 opacity-0"
            }
          `}
        >
          <div className="space-y-1">
            {group.items.map((item) => renderMenuItem(item, groupColor))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <LayoutContext.Provider value={layoutContextValue}>
      <div className="flex flex-col h-screen bg-background">
        <div className="flex-shrink-0 w-full relative z-40">
          <Header
            menuConfig={filteredMenuConfig}
            quickAccessItems={filteredQuickAccess}
            featuresConfig={featuresConfig}
          />
        </div>

        <div className="flex flex-1 overflow-hidden relative">
          {/* Desktop & Tablet Sidebar - Hidden on mobile */}
          <aside
            className={`
              hidden md:flex md:flex-col bg-gradient-to-b from-primary to-primary-dark
              text-text-on-primary transition-all duration-300 ease-in-out shadow-xl
              ${isMenuCollapsed ? "md:w-24" : "md:w-72 lg:w-72"}
              ${isTransitioning ? "overflow-hidden" : ""}
              h-full flex-shrink-0 relative
            `}
          >
            {/* Decorative Pattern */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-0 left-0 w-40 h-40 bg-text-on-primary rounded-full -translate-x-20 -translate-y-20" />
              <div className="absolute bottom-0 right-0 w-60 h-60 bg-text-on-primary rounded-full translate-x-20 translate-y-20" />
            </div>

            {/* Menu Controls */}
            <div
              className={`relative  p-3 flex items-center justify-between border-b border-text-on-primary border-opacity-20 ${
                isTransitioning ? "overflow-hidden" : ""
              }`}
            >
              {!isMenuCollapsed && (
                <>
                  <h3 className="font-bold text-lg whitespace-nowrap">
                    {t("common.menu")}
                  </h3>
                  {features.languageToggle && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          changeLanguage(language === "en" ? "hi" : "en")
                        }
                        className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
                        title="Change Language"
                      >
                        <TranslateIcon className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Collapse Toggle Button */}
              {features.menuCollapsing && (
                <button
                  onClick={toggleMenuCollapse}
                  className={`
                    ${isMenuCollapsed ? "mx-auto" : "ml-auto"}
                    bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg p-2
                    transition-all duration-300 transform hover:scale-110
                    flex-shrink-0
                  `}
                  title={t(
                    isMenuCollapsed
                      ? "menu.helper_texts.collapsed"
                      : "menu.helper_texts.expanded",
                  )}
                >
                  {isMenuCollapsed ? (
                    <ChevronRightIcon className="w-5 h-5" />
                  ) : (
                    <ChevronLeftIcon className="w-5 h-5" />
                  )}
                </button>
              )}
            </div>

            {/* Search Bar (when expanded) */}
            {!isMenuCollapsed && features.searchBar && (
              <div
                className={`relative p-3 border-b border-text-on-primary border-opacity-20 ${
                  isTransitioning ? "overflow-hidden" : ""
                }`}
              >
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-on-primary text-opacity-50" />
                  <input
                    type="text"
                    placeholder={t("common.search_menu")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 bg-white bg-opacity-20 rounded-lg text-text-on-primary placeholder-text-on-primary placeholder-opacity-50 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
                  />
                </div>
              </div>
            )}

            {/* Quick Access Section */}
            {features.quickAccess && filteredQuickAccess.length > 0 && (
              <div
                className={`relative z-10 border-b border-text-on-primary border-opacity-20 ${
                  isTransitioning ? "overflow-hidden" : ""
                }`}
              >
                {renderQuickAccess()}
              </div>
            )}

            {/* Main Navigation - Scrollable */}
            <nav
              className="relative  flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-white scrollbar-thumb-opacity-20"
              style={{
                minHeight: 0,
                flex: "1 1 auto",
              }}
            >
              <div className="py-3">
                {getFilteredItems().map(renderMenuGroup)}
              </div>
            </nav>

            {/* Profile & Logout Section - Fixed at Bottom */}
            <div
              className={`relative border-t border-text-on-primary border-opacity-20 bg-primary-dark bg-opacity-30 backdrop-blur-sm ${
                isMenuCollapsed ? "p-3" : "p-4"
              }`}
              style={{
                flexShrink: 0,
                flexGrow: 0,
              }}
            >
              {/* Action Buttons */}
              <div
                className={`flex ${
                  isMenuCollapsed ? "flex-col gap-2.5" : "gap-2.5 items-center"
                }`}
              >
                {/* Logout Button */}
                <button
                  className={`
                    group flex items-center justify-center transition-all duration-200
                    bg-danger hover:bg-danger-dark active:bg-danger-dark text-white
                    border border-danger-dark hover:border-danger-dark
                    shadow-md hover:shadow-lg active:shadow-sm
                    ${
                      isMenuCollapsed
                        ? "p-3 rounded-lg w-full order-2"
                        : "p-2.5 rounded-lg order-1"
                    }
                  `}
                  onClick={handleLogout}
                  title="Logout"
                >
                  <LogoutIcon
                    className={`${
                      isMenuCollapsed ? "w-5 h-5" : "w-4 h-4"
                    } flex-shrink-0 transition-transform group-hover:scale-110`}
                  />
                </button>

                {/* Profile Button */}
                <Link
                  to="/profile"
                  className={`
                    group flex items-center justify-center transition-all duration-200
                    bg-white bg-opacity-10 hover:bg-opacity-20 text-text-on-primary
                    border border-white border-opacity-20 hover:border-opacity-30
                    ${
                      isMenuCollapsed
                        ? "p-3 rounded-lg w-full order-1"
                        : "py-2.5 px-4 rounded-lg gap-2 flex-1 order-2"
                    }
                    ${
                      isActiveLink("/profile")
                        ? "bg-opacity-25 border-opacity-40 shadow-lg"
                        : "hover:shadow-md"
                    }
                  `}
                  title={isMenuCollapsed ? "Profile" : ""}
                >
                  <UserCircleIcon
                    className={`${
                      isMenuCollapsed ? "w-5 h-5" : "w-5 h-5"
                    } flex-shrink-0 transition-transform group-hover:scale-110`}
                  />
                  {!isMenuCollapsed && (
                    <span className="text-sm font-semibold tracking-wide">
                      PROFILE
                    </span>
                  )}
                </Link>
              </div>
            </div>
          </aside>

          {/* ============================================
              MAIN CONTENT AREA - UPDATED FOR SMART NAV
              ============================================ */}
          <main
            className="flex-1 overflow-y-auto overflow-x-hidden bg-background relative transition-[padding] duration-300"
            style={{
              // Dynamic padding - uses CSS variable from BottomTabNavigation
              // When nav is hidden, the variable becomes 0, so no wasted space
              paddingBottom: isMobile
                ? `var(${BOTTOM_NAV_HEIGHT_VAR}, ${BOTTOM_NAV_FALLBACK}px)`
                : undefined,
            }}
          >
            <div className="relative">
              <Outlet />
            </div>
          </main>
        </div>

        {/* ============================================
            SMART AUTO-HIDING BOTTOM NAVIGATION
            ============================================ */}
        <BottomTabNavigation featuresConfig={featuresConfig} />

        {/* Global styles */}
        <style jsx global>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateX(-10px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }

          .custom-scrollbar::-webkit-scrollbar-track {
            background: var(--color-neutral-light);
            border-radius: 3px;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: var(--color-neutral-dark);
            border-radius: 3px;
            opacity: 0.5;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            opacity: 0.8;
          }

          /* Prevent horizontal scroll globally */
          html,
          body {
            overflow-x: hidden;
            max-width: 100vw;
          }

          /* Safe area padding for iOS devices */
          .pb-safe {
            padding-bottom: env(safe-area-inset-bottom, 20px);
          }

          /* Ensure portal content doesn't cause overflow */
          body > div[data-react-portal] {
            pointer-events: none;
          }

          body > div[data-react-portal] > * {
            pointer-events: auto;
          }

          /* Container */
          .sidebar {
            display: flex;
            flex-direction: column;
            height: 100%;
          }

          /* Fixed sections (header, footer) */
          .header,
          .footer {
            flex-shrink: 0;
          }

          /* Scrollable content */
          .navigation {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
          }
        `}</style>
      </div>
    </LayoutContext.Provider>
  );
};

export default CollapsableLayout;
